/**
 * The per row detail drawer.
 *
 * Four sections and no more: the 90 day series with its two window totals, the price history,
 * the open purchase orders, and the supplier quotes. No lots, no certificates, no recent
 * orders. Those live on the batch ledger and on the order workbench, and putting a copy of
 * them here is how two screens start disagreeing.
 */
import { useEffect, useRef } from 'react';
import { detailHasMoney, type AnyStockPageRow } from './types';
import {
  AgeText,
  Drawer,
  EmptyState,
  ErrorState,
  MoneyText,
  Skeleton,
  Sparkline,
  StarIcon,
} from '@/components';
import { ApiProblem } from '@/lib/api';
import { formatCount, shortDate } from '@/lib/format';
import { useStockRowDetail } from './queries';

export interface StockRowDrawerProps {
  row: AnyStockPageRow | null;
  /** Which section to scroll to on open. The best price cell asks for the quotes. */
  section?: 'quotes' | undefined;
  onClose: () => void;
}

export function StockRowDrawer({ row, section, onClose }: StockRowDrawerProps) {
  const detail = useStockRowDetail(row ? row.product_id : null);
  const quotesRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!row || section !== 'quotes' || !detail.data) return;
    quotesRef.current?.scrollIntoView({ block: 'start' });
  }, [detail.data, row, section]);

  if (!row) return null;

  const problem = detail.error instanceof ApiProblem ? detail.error : null;
  const title = `${row.name}${row.size_label ? ` ${row.size_label}` : ''}`;

  return (
    <Drawer open onClose={onClose} title={title} id={`stock-detail-${row.product_id}`}>
      <p className="o-mono o-muted stock-drawer__sku">{row.sku ?? 'no SKU'}</p>

      {detail.isLoading ? <Skeleton shape="card" lines={4} label="Loading detail" /> : null}

      {problem ? (
        <ErrorState
          message={problem.message}
          requestId={problem.shortRequestId}
          onRetry={() => void detail.refetch()}
        />
      ) : null}

      {detail.data ? (
        <>
          <section className="stock-drawer__section" aria-labelledby="stock-detail-series">
            <h3 className="stock-drawer__heading" id="stock-detail-series">
              90 day units
            </h3>
            <Sparkline
              points={detail.data.series_90d.map((point) => point.units)}
              label="90 day units by day"
            />
            <dl className="stock-drawer__facts">
              <div>
                <dt>Last 30 days</dt>
                <dd className="o-num">{formatCount(detail.data.units_30d)}</dd>
              </div>
              <div>
                <dt>Prior 30 days</dt>
                <dd className="o-num">{formatCount(detail.data.units_prior_30d)}</dd>
              </div>
            </dl>
          </section>

          {/* A packer's detail has no price history and no quotes: not empty, absent. The
              two sections are not rendered rather than rendered with nothing in them. */}
          {detailHasMoney(detail.data) ? (
          <section className="stock-drawer__section" aria-labelledby="stock-detail-prices">
            <h3 className="stock-drawer__heading" id="stock-detail-prices">
              Price history
            </h3>
            {detail.data.price_history.length === 0 ? (
              <EmptyState message="No recorded price change for this SKU." />
            ) : (
              <table className="stock-table">
                <caption className="o-visually-hidden">Price history</caption>
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">From</th>
                    <th scope="col">To</th>
                    <th scope="col">Who</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.price_history.map((change) => (
                    <tr key={`${change.at}-${change.new_cents}`}>
                      <td>
                        <AgeText at={change.at} />
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={change.old_cents} showCents nullText="no prior" />
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={change.new_cents} showCents />
                      </td>
                      <td>{change.actor ?? 'unknown'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          ) : null}

          <section className="stock-drawer__section" aria-labelledby="stock-detail-pos">
            <h3 className="stock-drawer__heading" id="stock-detail-pos">
              Open purchase orders
            </h3>
            {detail.data.open_pos.length === 0 ? (
              <EmptyState message="Nothing on order for this SKU." />
            ) : (
              <table className="stock-table">
                <caption className="o-visually-hidden">Open purchase orders</caption>
                <thead>
                  <tr>
                    <th scope="col">PO</th>
                    <th scope="col">Supplier</th>
                    <th scope="col">Status</th>
                    <th scope="col">Ordered</th>
                    <th scope="col">Received</th>
                    <th scope="col">ETA</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.open_pos.map((po) => (
                    <tr key={po.po_id}>
                      <td className="o-mono">{po.po_number}</td>
                      <td>{po.supplier_name ?? 'unknown'}</td>
                      <td>{po.status}</td>
                      <td className="stock-num o-num">{formatCount(po.qty_ordered)}</td>
                      <td className="stock-num o-num">{formatCount(po.qty_received)}</td>
                      <td>{po.eta ? shortDate(po.eta) : 'no ETA'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {detailHasMoney(detail.data) ? (
          <section
            className="stock-drawer__section"
            aria-labelledby="stock-detail-quotes"
            ref={quotesRef}
          >
            <h3 className="stock-drawer__heading" id="stock-detail-quotes">
              Supplier quotes
            </h3>
            {detail.data.quotes.length === 0 ? (
              <EmptyState message="No supplier quote on file for this SKU." />
            ) : (
              <table className="stock-table">
                <caption className="o-visually-hidden">Supplier quotes</caption>
                <thead>
                  <tr>
                    <th scope="col">Supplier</th>
                    <th scope="col">Unit</th>
                    <th scope="col">Effective</th>
                    <th scope="col">Landed</th>
                    <th scope="col">MOQ</th>
                    <th scope="col">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data.quotes.map((quote) => (
                    <tr key={quote.supplier_price_id}>
                      <td>
                        {quote.supplier_name}
                        {quote.negotiated ? (
                          <>
                            {' '}
                            <StarIcon />
                            <span className="o-visually-hidden">negotiated</span>
                          </>
                        ) : null}
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={quote.unit_price_cents} showCents />
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={quote.effective_unit_cents} showCents />
                      </td>
                      <td className="stock-num">
                        <MoneyText
                          cents={quote.landed_estimate_cents}
                          showCents
                          nullText="no freight rate"
                        />
                      </td>
                      <td className="stock-num o-num">
                        {quote.moq === null ? 'none' : formatCount(quote.moq)}
                      </td>
                      <td>
                        <AgeText at={quote.updated_at} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          ) : null}
        </>
      ) : null}
    </Drawer>
  );
}
